/**
 * Offline sync engine runtime. The pure server-push lives in inspectionSyncCore
 * (injected api, unit-tested); here we upload queued photo blobs, resolve
 * markers, mark synced, and wire the triggers using the real apiClient.
 */
import { apiClient } from './apiClient';
import {
  getAllLocalDrafts,
  loadLocalDraft,
  saveLocalDraft,
  markSynced,
  markSyncFailure,
  clearSyncBlock,
  deleteLocalDraft,
} from './localDraftStore';
import {
  makeMarker,
  replaceMarkerEverywhere,
  getAllQueuedPhotos,
  deleteQueuedPhoto,
  deleteQueuedPhotosForDraft,
  selectOrphanPhotos,
} from './photoQueue';
import { pushLocalDraftToServer, findServerMatch, type ServerReportLite } from './inspectionSyncCore';

export { pushLocalDraftToServer, findServerMatch } from './inspectionSyncCore';
export type { ApiLike, ServerReportLite } from './inspectionSyncCore';

// ---- runtime orchestration -------------------------------------------------

const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

/** Remove a local draft together with any photo blobs it still owns. */
async function purgeDraft(localId: string): Promise<void> {
  await deleteQueuedPhotosForDraft(localId);
  await deleteLocalDraft(localId);
}

/**
 * Upload this draft's queued photos (resolving markers), then push it to the
 * server. `isDraft=false` submits and clears the local draft. Returns the server
 * report id, or null if it couldn't run. Requires connectivity.
 */
export async function syncDraft(
  localId: string,
  opts: { isDraft: boolean } = { isDraft: true },
): Promise<string | null> {
  let draft = await loadLocalDraft(localId);
  if (!draft) return null;

  const queued = (await getAllQueuedPhotos()).filter((q) => q.localDraftId === localId);
  for (const q of queued) {
    const filename = q.path.split('/').pop() ?? 'photo.jpg';
    const marker = makeMarker(q.id);
    const { url } = await apiClient.upload(q.bucket, q.blob, { path: q.path, filename });
    draft = replaceMarkerEverywhere(draft, marker, url);
    await saveLocalDraft(draft);
    await deleteQueuedPhoto(q.id);
    emitPhotoResolved(marker, url); // let an open form swap the preview live
  }

  const { serverReportId } = await pushLocalDraftToServer(draft, { api: apiClient, isDraft: opts.isDraft });
  if (opts.isDraft) await markSynced(localId, serverReportId);
  else await purgeDraft(localId); // submitted — drop the draft and any straggler photos
  notifyPending();
  return serverReportId;
}

let inFlight = false;

/** Background: push every dirty local draft (as a draft). Single-flight. */
export async function syncNow(reason = 'manual'): Promise<void> {
  if (inFlight || !isOnline()) return;
  inFlight = true;
  try {
    const drafts = await getAllLocalDrafts();

    // The server report list is the source of truth for "is this work already
    // saved?". Fetched at most once per pass, and only when a draft is stuck (so
    // healthy syncs never pay for it). Used to auto-clear redundant local drafts.
    let serverReports: ServerReportLite[] | null = null;
    const loadServerReports = async (): Promise<ServerReportLite[]> => {
      if (serverReports == null) {
        try {
          serverReports = await apiClient.get<ServerReportLite[]>('/api/reports');
        } catch {
          serverReports = []; // best-effort; a failed reconcile just defers
        }
      }
      return serverReports;
    };
    // Auto-recover: if the work is already submitted on the server, the local
    // draft is redundant — drop it silently. Returns true when it cleared one.
    const reconcile = async (d: typeof drafts[number]): Promise<boolean> => {
      const match = findServerMatch(
        { serverReportId: d.serverReportId, jobSiteId: d.jobSiteId, month: d.month },
        await loadServerReports(),
      );
      if (!match) return false;
      await purgeDraft(d.localId);
      console.warn(`[sync] draft ${d.localId} already on server as ${match.id} (submitted); auto-cleared.`);
      return true;
    };

    for (const draft of drafts) {
      if (!draft.dirty) continue;
      // A draft auto-sync has given up on still self-heals: each pass we re-check
      // whether its report has since landed on the server and, if so, clear it —
      // no user action. Only a draft with genuinely unsynced work and no server
      // copy stays blocked (the manual Retry/Discard fallback).
      if (draft.syncBlocked) {
        await reconcile(draft);
        continue;
      }
      try {
        // A draft the tech submitted offline must be pushed as a SUBMITTED
        // report; everything else syncs as a draft. (Old records without the
        // flag default to draft.)
        await syncDraft(draft.localId, { isDraft: !draft.pendingSubmit });
      } catch (err) {
        const status = (err as { status?: number } | undefined)?.status;
        if (status === 401) {
          // Session lapsed (or a re-auth is already in flight). This is an AUTH
          // problem, not a bad draft — counting it would flip every dirty draft
          // to syncBlocked within ~2.5 min of 30s interval retries and hand the
          // tech a manual Retry/Discard for something signing in fixes. Leave the
          // draft dirty and untouched; the next pass after re-auth picks it up.
          console.warn(`[sync] draft ${draft.localId} deferred — sign-in required.`);
        } else if (status === 403) {
          // The report is already submitted on the server (a User can't edit a
          // non-draft report). The submit went through — stop retrying and drop
          // the local draft (and its photos), exactly as a successful submit
          // would. Without this the draft stays dirty and re-submits every
          // interval, looping on 403.
          await purgeDraft(draft.localId);
          console.warn(`[sync] draft ${draft.localId} already submitted (403); cleared.`);
        } else {
          // Failure. Count it; after MAX_SYNC_ATTEMPTS the draft is blocked from
          // auto-retry (instead of looping invisibly forever). But first, once a
          // draft has failed a couple of times, check whether the work is already
          // on the server (submitted earlier/elsewhere) and auto-clear it if so —
          // so the common "it's actually saved, my device just won't stop warning"
          // case never needs a human.
          const message = (err as { message?: string } | undefined)?.message ?? String(err);
          const updated = await markSyncFailure(draft.localId, message);
          if ((updated?.syncAttempts ?? 0) >= 2 && (await reconcile(draft))) continue;
          console.warn(
            `[sync] draft ${draft.localId} failed (${reason}, attempt ${updated?.syncAttempts ?? '?'})` +
              `${updated?.syncBlocked ? ' — blocked, awaiting manual action' : ''}:`,
            err,
          );
        }
      }
    }
  } finally {
    inFlight = false;
    await sweepOrphanPhotos(); // drop photo blobs whose draft is already gone
    notifyPending();
  }
}

/**
 * Delete queued photo blobs whose owning draft no longer exists — local cleanup,
 * no network needed. Without this, photos left behind when a draft was removed
 * (e.g. a 403 clear before this fix) would inflate the pending badge forever.
 * Returns the number removed.
 */
export async function sweepOrphanPhotos(): Promise<number> {
  const [drafts, photos] = await Promise.all([getAllLocalDrafts(), getAllQueuedPhotos()]);
  const live = new Set(drafts.map((d) => d.localId));
  const orphans = selectOrphanPhotos(photos, live);
  for (const o of orphans) await deleteQueuedPhoto(o.id);
  if (orphans.length) {
    console.warn(`[sync] swept ${orphans.length} orphaned photo blob(s).`);
    notifyPending();
  }
  return orphans.length;
}

/**
 * Manual "Retry": clear the block on every stuck draft and force an immediate
 * sync. Also sweeps orphan photos. Safe to call when online or offline.
 */
export async function retryPendingNow(): Promise<void> {
  const drafts = await getAllLocalDrafts();
  await Promise.all(drafts.filter((d) => d.syncBlocked).map((d) => clearSyncBlock(d.localId)));
  await sweepOrphanPhotos();
  notifyPending();
  await syncNow('manual-retry');
}

/**
 * Manual "Discard": permanently remove drafts that auto-sync has given up on,
 * plus any orphan photos. Destructive — the UI confirms first. Returns the count
 * of drafts discarded.
 */
export async function discardBlocked(): Promise<number> {
  const drafts = await getAllLocalDrafts();
  const blocked = drafts.filter((d) => d.syncBlocked);
  for (const d of blocked) await purgeDraft(d.localId);
  await sweepOrphanPhotos();
  notifyPending();
  return blocked.length;
}

export interface PendingCounts {
  unsyncedDrafts: number;
  queuedPhotos: number;
  /** Subset of unsyncedDrafts that auto-sync gave up on (need manual action). */
  blockedDrafts: number;
}

export async function getPendingCounts(): Promise<PendingCounts> {
  const [drafts, photos] = await Promise.all([getAllLocalDrafts(), getAllQueuedPhotos()]);
  return {
    unsyncedDrafts: drafts.filter((d) => d.dirty).length,
    queuedPhotos: photos.length,
    blockedDrafts: drafts.filter((d) => d.dirty && d.syncBlocked).length,
  };
}

// Lightweight pub/sub so the connection indicator can refresh after a sync.
const listeners = new Set<() => void>();
export function subscribePending(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
/**
 * Tell subscribers (e.g. the connection indicator) to re-read the pending
 * counts now. The workflow calls this after persisting an offline save so the
 * "items waiting to upload" badge updates immediately, without waiting for the
 * next online/interval trigger.
 */
export function notifyPendingChanged(): void {
  notifyPending();
}
function notifyPending(): void {
  for (const cb of listeners) {
    try {
      cb();
    } catch {
      /* ignore */
    }
  }
}

// Emitted when a queued offline photo is uploaded, so an open inspection form can
// swap its local-photo:// marker for the real URL without a reload.
const photoResolvedListeners = new Set<(marker: string, url: string) => void>();
export function subscribePhotoResolved(cb: (marker: string, url: string) => void): () => void {
  photoResolvedListeners.add(cb);
  return () => photoResolvedListeners.delete(cb);
}
function emitPhotoResolved(marker: string, url: string): void {
  for (const cb of photoResolvedListeners) {
    try {
      cb(marker, url);
    } catch {
      /* ignore */
    }
  }
}

let started = false;
/** Wire global sync triggers once (online event + periodic + initial). */
export function startSyncTriggers(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('online', () => void syncNow('online'));
  setInterval(() => {
    if (isOnline()) void syncNow('interval');
  }, 30000);
  // Local cleanup runs regardless of connectivity, so do it up front in case the
  // device boots offline with orphaned blobs lingering from a previous session.
  void sweepOrphanPhotos();
  void syncNow('startup');
  // Phase-2 seam: a service-worker 'sync' handler would also call syncNow().
}
